import {
  BodyText,
  Button,
  Callout,
  Separator,
  Tooltip,
  toast,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { useParams } from "@tanstack/react-router";
import { CirclePlus } from "lucide-react";
import { Fragment, useState } from "react";
import { SectionContent } from "../components/SectionContent";
import { SectionHeader } from "../components/SectionHeader";
import { HospitalizationForm } from "./components/HospitalizationForm";
import { HospitalizationItem } from "./components/HospitalizationItem";
import { HospitalizationSkeleton } from "./components/HospitalizationSkeleton";
import { useAddHospitalization, useGetHospitalization } from "./network";

export const Hospitalizations = () => {
  const { patientId } = useParams({ strict: false });
  const [isAddingHospitalization, setIsAddingHospitalization] = useState(false);
  const {
    data,
    isLoading: isLoadingHospitalization,
    isError,
    refetch,
  } = useGetHospitalization({ patientId: patientId as string });
  const { mutate: addHospitalization, isPending: isSubmitting } =
    useAddHospitalization({
      patientId: patientId as string,
      onSuccess: () => {
        toast.success("Hospitalization added successfully");
        setIsAddingHospitalization(false);
      },
      onError: (error) => {
        toast.error(`Failed to add hospitalization: ${error.message}`);
      },
    });

  const showSkeleton = isLoadingHospitalization;
  const showError = !showSkeleton && (isError || !data);
  const showEmpty =
    !showSkeleton &&
    !showError &&
    !isAddingHospitalization &&
    data &&
    data.encounters.length === 0;
  const showHospitalizations =
    !showSkeleton && !showError && data && data.encounters.length > 0;

  return (
    <div className="w-full h-full flex flex-col">
      <SectionHeader
        title="Hospitalizations"
        actionButtons={[
          <Tooltip tooltipContentText="Add" side="bottom" key="add">
            <Button
              variant="icon"
              className="m-0 p-0"
              onClick={() => {
                trackEvent(TRACKERS.ADD_HOSPITALIZATION);
                setIsAddingHospitalization(true);
              }}
              aria-label="Add hospitalization"
            >
              <CirclePlus className="stroke-link h-4 w-4" />
            </Button>
          </Tooltip>,
        ]}
      />
      <SectionContent>
        {isAddingHospitalization && (
          <>
            <HospitalizationForm
              onCancel={() => {
                trackEvent(TRACKERS.CANCEL_HOSPITALIZATIONS);
                setIsAddingHospitalization(false);
              }}
              onSubmit={(data) => {
                trackEvent(TRACKERS.SAVE_HOSPITALIZATIONS);
                addHospitalization({
                  patientId: patientId as string,
                  periodStartDate: data.periodStartDate.toISOString(),
                  reasonCode: {
                    coding: [
                      {
                        code: data.reasonCode,
                        display: data.reasonDisplay,
                        system: data.reasonSystem,
                      },
                    ],
                    text: data.reasonDisplay,
                  },
                });
              }}
              isSubmitting={isSubmitting}
            />
            <Separator className="w-full" />
          </>
        )}
        {showSkeleton && <HospitalizationSkeleton />}
        {showError && (
          <div className="flex flex-col gap-2 py-2 w-full">
            <Callout
              className="w-full cursor-pointer"
              variant="danger"
              title="Unable to load hospitalizations!"
              description="Please click here to try again."
              onClick={() => refetch()}
            />
          </div>
        )}
        {showEmpty && (
          <div className="flex gap-2 py-2 w-full">
            <BodyText size="sm" textColor="tertiary">
              No hospitalizations documented
            </BodyText>
          </div>
        )}
        {showHospitalizations && (
          <div>
            {data.encounters.map((hospitalization) => (
              <Fragment key={hospitalization.id}>
                <HospitalizationItem
                  key={hospitalization.id}
                  hospitalization={hospitalization}
                />
                <Separator className="w-full" />
              </Fragment>
            ))}
          </div>
        )}
      </SectionContent>
    </div>
  );
};
